import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import {
  Home,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Calendar,
  ChevronDown,
  ChevronRight,
  Search,
  Filter,
  RefreshCw,
  X,
  Check,
  AlertCircle,
  Loader2,
  MessageSquare,
  UserPlus,
  ArrowRight,
  Clock,
  Building2,
  FileText,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Pencil,
  Trash2,
  Archive,
  RotateCcw,
  BarChart3,
  Sparkles,
  Target,
  Trophy,
  Send,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  Users,
  Bed,
  Bath,
  Square,
  Tag,
  CalendarDays,
  Handshake,
  FileCheck,
  Plus,
} from "lucide-react";
import {
  fetchListings,
  fetchListingStats,
  fetchListing,
  createListing,
  updateListingStatus,
  assignListing,
  addListingNote,
  updateListingNote,
  deleteListingNote,
  toggleNoteReaction,
  closeListing,
  fetchArchivedListings,
  archiveListing,
  restoreListing,
  fetchMarketplaceActivity,
  fetchTeamContext,
  fetchListingsReports,
  fetchListingsReport,
  generateListingsReport,
  ARCHIVE_REASONS,
  STATUS_CONFIG,
  SOURCE_CONFIG,
  LISTING_STATUSES,
  getValidTransitions,
  formatListingDate,
  formatPhoneNumber,
  formatCurrency,
  formatCurrencyAbbreviated,
  calculateDaysOnMarket,
} from "../services/ListingsService";
import MentionInput, { renderTextWithMentions } from "./MentionInput";

// Quick reaction emojis for notes
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉'];

// Sales-focused celebration messages for closed deals
const SALES_CELEBRATION_MESSAGES = [
  "SOLD! 🏠",
  "Deal Closed! 💪",
  "Commission Time! 💰",
  "Another One Sold! 🎯",
  "Closing Champion! 🏆",
  "Keys Handed Over! 🔑",
  "Happy Homeowners! 🎉",
  "Market Master! 📈",
  "Listing Legend! ⭐",
  "Real Estate Rockstar! 🚀",
  "Top Producer! 💎",
  "Dreams Delivered! 🌟",
  "Deal Maker! 🤝",
  "Sold Sign Up! 🪧",
  "Escrow Closed! ✅",
];

// Sales Celebration Hook
const useSalesCelebration = () => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [recycleConfetti, setRecycleConfetti] = useState(false);
  const [flyer, setFlyer] = useState(null);
  const [celebrationMessage, setCelebrationMessage] = useState(null);

  const triggerCelebration = useCallback(() => {
    setShowConfetti(false);
    setFlyer(null);
    setCelebrationMessage(null);

    setTimeout(() => {
      setShowConfetti(true);
      setRecycleConfetti(true);
    }, 10);

    const { innerHeight } = window;
    const flyerTypes = ['🏠', '🔑', '💰', '🎯', '🏆', '⭐', '🎉'];
    const flyerEmoji = flyerTypes[Math.floor(Math.random() * flyerTypes.length)];
    setFlyer({ emoji: flyerEmoji, y: Math.random() * (innerHeight * 0.5) + 200 });

    const message = SALES_CELEBRATION_MESSAGES[Math.floor(Math.random() * SALES_CELEBRATION_MESSAGES.length)];
    setCelebrationMessage(message);

    setTimeout(() => setRecycleConfetti(false), 2500);
    setTimeout(() => {
      setShowConfetti(false);
      setFlyer(null);
      setCelebrationMessage(null);
    }, 5000);
  }, []);

  return {
    triggerCelebration,
    celebrationState: { showConfetti, recycleConfetti, flyer, celebrationMessage }
  };
};

// Sales Celebration Overlay Component
const SalesCelebrationOverlay = ({ state }) => {
  const { width, height } = useWindowSize();

  if (!state) return null;
  const { showConfetti, recycleConfetti, flyer, celebrationMessage } = state;
  if (!showConfetti && !flyer && !celebrationMessage) return null;

  return (
    <>
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
          <Confetti
            width={width}
            height={height}
            numberOfPieces={400}
            recycle={recycleConfetti}
            gravity={0.15}
            wind={0.01}
            colors={['#10B981', '#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4']}
          />
        </div>
      )}
      <AnimatePresence>
        {flyer && (
          <motion.div
            initial={{ x: -150, y: flyer.y, rotate: 10, scale: 0.5 }}
            animate={{ x: width + 150, rotate: -10, scale: 1.2 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 3.5, ease: "easeInOut" }}
            className="fixed z-[100] pointer-events-none text-6xl drop-shadow-2xl"
          >
            {flyer.emoji}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {celebrationMessage && (
          <div className="fixed inset-0 flex items-center justify-center z-[100] pointer-events-none">
            <motion.div
              initial={{ scale: 0.5, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 1.5, opacity: 0, filter: 'blur(10px)' }}
              transition={{ type: "spring", bounce: 0.5 }}
              className="text-6xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-sky-500 to-violet-500 drop-shadow-[0_0_30px_rgba(16,185,129,0.5)] tracking-tighter text-center px-4"
            >
              {celebrationMessage}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

// Success Modal Component
const SuccessModal = ({ isOpen, onClose, type, data }) => {
  if (!isOpen) return null;

  const isSold = type === "sold";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 20 }}
        transition={{ type: "spring", bounce: 0.4 }}
        className="relative w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="h-2 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />
        <div className="p-6 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", bounce: 0.5 }}
            className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center bg-gradient-to-br from-emerald-400 to-teal-500"
          >
            <Home className="w-10 h-10 text-white" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-2xl font-bold text-slate-900 dark:text-white mb-2"
          >
            {isSold ? "Listing Sold!" : "Success!"}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-slate-500 dark:text-slate-400 mb-6"
          >
            {isSold
              ? "Congratulations! Your listing has been marked as sold."
              : "Operation completed successfully."}
          </motion.p>
          {data?.sale_price && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 mb-6 text-left"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500 dark:text-slate-400">Sale Price</span>
                <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(data.sale_price)}
                </span>
              </div>
            </motion.div>
          )}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            onClick={onClose}
            className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-lg shadow-emerald-500/25 transition-all"
          >
            Continue
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Stats Card Component
const StatsCard = ({ label, value, color, icon: Icon, isActive, onClick }) => {
  const colorClasses = {
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    blue: "bg-blue-500",
    violet: "bg-violet-500",
    orange: "bg-orange-500",
    slate: "bg-slate-500",
  };

  return (
    <button
      onClick={onClick}
      className={`flex-1 min-w-[140px] p-4 rounded-xl border transition-all ${
        isActive
          ? "bg-slate-900 dark:bg-white border-slate-900 dark:border-white"
          : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="text-left">
          <p className={`text-2xl font-bold ${isActive ? "text-white dark:text-slate-900" : "text-slate-900 dark:text-white"}`}>
            {value}
          </p>
          <p className={`text-xs font-medium ${isActive ? "text-slate-300 dark:text-slate-600" : "text-slate-500 dark:text-slate-400"}`}>
            {label}
          </p>
        </div>
      </div>
    </button>
  );
};

// Status Badge Component
const StatusBadge = ({ status, size = "sm" }) => {
  const normalizedStatus = (status || "active").toLowerCase();
  const config = STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.active;
  const sizeClasses = size === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm";

  return (
    <span className={`inline-flex items-center rounded-full font-medium ${sizeClasses} ${config.bgClass} ${config.textClass}`}>
      {config.label}
    </span>
  );
};

// Source Badge Component
const SourceBadge = ({ source, size = "sm" }) => {
  const normalizedSource = (source || "").toLowerCase();
  const config = SOURCE_CONFIG[normalizedSource] || SOURCE_CONFIG.unknown;
  const sizeClasses = size === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm";

  return (
    <span className={`inline-flex items-center rounded-full font-medium ${sizeClasses} ${config.bgClass} ${config.textClass}`}>
      {config.label}
    </span>
  );
};

// Listing Row Component
const ListingRow = ({ listing, isSelected, onClick, userEmail }) => {
  const isOwnListing = listing.is_own_listing === true ||
    listing.assigned_to === userEmail ||
    listing.owner_email === userEmail;
  const ownerDisplay = isOwnListing ? "You" : (listing.owner_name || listing.assigned_to || "Team");
  const daysOnMarket = calculateDaysOnMarket(listing.listing_date);

  return (
    <tr
      onClick={onClick}
      className={`cursor-pointer transition-colors ${
        isSelected
          ? "bg-emerald-50 dark:bg-emerald-900/20"
          : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
      }`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white">
            <Home className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-slate-900 dark:text-white truncate max-w-[200px]">
                {listing.property_address || "Unknown Address"}
              </p>
              {!isOwnListing && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
                  Team
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {listing.city}, {listing.state} {listing.zip_code}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
          {formatCurrency(listing.listing_price)}
        </p>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={listing.status} />
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-slate-600 dark:text-slate-400">{ownerDisplay}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
          {listing.bedrooms && (
            <span className="flex items-center gap-1">
              <Bed className="w-3 h-3" /> {listing.bedrooms}
            </span>
          )}
          {listing.bathrooms && (
            <span className="flex items-center gap-1">
              <Bath className="w-3 h-3" /> {listing.bathrooms}
            </span>
          )}
          {listing.square_feet && (
            <span className="flex items-center gap-1">
              <Square className="w-3 h-3" /> {listing.square_feet.toLocaleString()}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-slate-500 dark:text-slate-400">{daysOnMarket} days</span>
      </td>
      <td className="px-4 py-3">
        <SourceBadge source={listing.listing_source} />
      </td>
      <td className="px-4 py-3 text-right">
        <ChevronRight className="w-4 h-4 text-slate-400 inline" />
      </td>
    </tr>
  );
};

// Delete Note Confirmation Modal
const DeleteNoteModal = ({ isOpen, onClose, onConfirm, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={(e) => e.target === e.currentTarget && !isDeleting && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-sm overflow-hidden"
      >
        <div className="p-6">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white text-center mb-2">
            Delete Note?
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-6">
            This action cannot be undone.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="flex-1 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              className="flex-1 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Add Listing Modal Component
const AddListingModal = ({ isOpen, onClose, onAdd, isAdding }) => {
  const [formData, setFormData] = useState({
    address: "",
    city: "",
    state: "",
    zip_code: "",
    listing_price: "",
    bedrooms: "",
    bathrooms: "",
    square_feet: "",
    property_type: "single_family",
    seller_name: "",
    seller_phone: "",
    seller_email: "",
    mls_number: "",
    source: "direct",
    notes: "",
  });
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.address.trim()) {
      setError("Property address is required");
      return;
    }

    try {
      await onAdd({
        ...formData,
        listing_price: formData.listing_price ? parseFloat(formData.listing_price.replace(/[^0-9.]/g, "")) : null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null,
        square_feet: formData.square_feet ? parseInt(formData.square_feet.replace(/[^0-9]/g, "")) : null,
        status: "active",
      });
      setFormData({
        address: "",
        city: "",
        state: "",
        zip_code: "",
        listing_price: "",
        bedrooms: "",
        bathrooms: "",
        square_feet: "",
        property_type: "single_family",
        seller_name: "",
        seller_phone: "",
        seller_email: "",
        mls_number: "",
        source: "direct",
        notes: "",
      });
      onClose();
    } catch (err) {
      setError(err.message || "Failed to add listing");
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Add New Listing</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Property Address */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-500" />
              Property Address
            </h3>
            <div className="space-y-3">
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Street Address *"
                className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <div className="grid grid-cols-3 gap-3">
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                  className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="State"
                  className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <input
                  type="text"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  placeholder="ZIP Code"
                  className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Property Details */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Home className="w-4 h-4 text-emerald-500" />
              Property Details
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">Listing Price</label>
                <input
                  type="text"
                  name="listing_price"
                  value={formData.listing_price}
                  onChange={handleChange}
                  placeholder="$0"
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">Property Type</label>
                <select
                  name="property_type"
                  value={formData.property_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="single_family">Single Family</option>
                  <option value="condo">Condo</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="multi_family">Multi-Family</option>
                  <option value="land">Land</option>
                  <option value="commercial">Commercial</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">Bedrooms</label>
                <input
                  type="number"
                  name="bedrooms"
                  value={formData.bedrooms}
                  onChange={handleChange}
                  placeholder="0"
                  min="0"
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">Bathrooms</label>
                <input
                  type="number"
                  name="bathrooms"
                  value={formData.bathrooms}
                  onChange={handleChange}
                  placeholder="0"
                  min="0"
                  step="0.5"
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">Square Feet</label>
                <input
                  type="text"
                  name="square_feet"
                  value={formData.square_feet}
                  onChange={handleChange}
                  placeholder="0"
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">MLS Number</label>
                <input
                  type="text"
                  name="mls_number"
                  value={formData.mls_number}
                  onChange={handleChange}
                  placeholder="MLS#"
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Seller Information */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-500" />
              Seller Information
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                name="seller_name"
                value={formData.seller_name}
                onChange={handleChange}
                placeholder="Seller Name"
                className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <input
                type="tel"
                name="seller_phone"
                value={formData.seller_phone}
                onChange={handleChange}
                placeholder="Phone"
                className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <input
                type="email"
                name="seller_email"
                value={formData.seller_email}
                onChange={handleChange}
                placeholder="Email"
                className="col-span-2 px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Source */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Tag className="w-4 h-4 text-emerald-500" />
              Listing Source
            </h3>
            <select
              name="source"
              value={formData.source}
              onChange={handleChange}
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="direct">Direct</option>
              <option value="referral">Referral</option>
              <option value="mls">MLS</option>
              <option value="zillow">Zillow</option>
              <option value="realtor">Realtor.com</option>
              <option value="fsbo">FSBO</option>
            </select>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-emerald-500" />
              Notes
            </h3>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any notes about this listing..."
              rows={3}
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isAdding}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isAdding ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add Listing
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Assign Listing Modal Component
const AssignListingModal = ({ isOpen, onClose, onAssign, teamMembers, currentAssignee, isAssigning }) => {
  const [selectedMember, setSelectedMember] = useState(currentAssignee || "");
  const [selectedRole, setSelectedRole] = useState("listing_agent");
  const [sendNotification, setSendNotification] = useState(true);

  if (!isOpen) return null;

  const handleAssign = () => {
    if (!selectedMember) return;
    onAssign(selectedMember, selectedRole, sendNotification);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={(e) => e.target === e.currentTarget && !isAssigning && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-sm overflow-hidden"
      >
        <div className="p-6">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <UserPlus className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white text-center mb-4">
            Assign Listing
          </h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Assign to
            </label>
            <select
              value={selectedMember}
              onChange={(e) => setSelectedMember(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Select team member...</option>
              {teamMembers?.map((member) => (
                <option key={member.email} value={member.email}>
                  {member.name || member.email}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Role
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="listing_agent">Listing Agent</option>
              <option value="buyers_agent">Buyer's Agent</option>
              <option value="co_listing">Co-Listing Agent</option>
            </select>
          </div>
          <label className="flex items-center gap-3 mb-6 cursor-pointer">
            <input
              type="checkbox"
              checked={sendNotification}
              onChange={(e) => setSendNotification(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              Send email notification
            </span>
          </label>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isAssigning}
              className="flex-1 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedMember || isAssigning}
              className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isAssigning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Assigning...
                </>
              ) : (
                "Assign"
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Listing Detail Panel Component
const ListingDetailPanel = ({
  listing,
  onClose,
  onStatusChange,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  onToggleReaction,
  onAssign,
  onMarkSold,
  onArchive,
  onShareWithLender,
  onShareWithAgent,
  teamMembers,
  userEmail,
}) => {
  const [activeTab, setActiveTab] = useState("details");
  const [newNote, setNewNote] = useState("");
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isMarkingSold, setIsMarkingSold] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [isDeletingNote, setIsDeletingNote] = useState(null);
  const [pendingDeleteNoteIndex, setPendingDeleteNoteIndex] = useState(null);
  const [showQuickReactions, setShowQuickReactions] = useState(null);
  const [noteError, setNoteError] = useState(null);

  const daysOnMarket = calculateDaysOnMarket(listing.listing_date);

  const handleReaction = async (noteIndex, emoji) => {
    try {
      await onToggleReaction(listing.listing_id, noteIndex, emoji);
      setShowQuickReactions(null);
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
    }
  };

  const hasActualNoteContent = (text) => {
    if (!text?.trim()) return false;
    const contentWithoutMentions = text.replace(/@\w+(\.\w+)*(@[\w.]+)?/g, '').trim();
    return contentWithoutMentions.length > 0;
  };

  const canEditNote = (note) => {
    if (note.is_system || note.system || note.type === 'system') return false;
    const noteAuthor = note.author || note.created_by || note.updated_by;
    return noteAuthor?.toLowerCase() === userEmail?.toLowerCase();
  };

  const handleEditNote = async (noteIndex) => {
    if (!editingNoteText.trim()) return;
    setIsEditingNote(true);
    setNoteError(null);
    try {
      await onUpdateNote(listing.listing_id, noteIndex, editingNoteText);
      setEditingNoteIndex(null);
      setEditingNoteText("");
    } catch (err) {
      setNoteError(err.message || "Failed to update note");
      setEditingNoteIndex(null);
      setEditingNoteText("");
    } finally {
      setIsEditingNote(false);
    }
  };

  const handleDeleteNote = async () => {
    if (pendingDeleteNoteIndex === null) return;
    setIsDeletingNote(pendingDeleteNoteIndex);
    setNoteError(null);
    try {
      await onDeleteNote(listing.listing_id, pendingDeleteNoteIndex);
      setPendingDeleteNoteIndex(null);
    } catch (err) {
      setNoteError(err.message || "Failed to delete note");
      setPendingDeleteNoteIndex(null);
    } finally {
      setIsDeletingNote(null);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setIsUpdatingStatus(true);
    setShowStatusDropdown(false);
    try {
      await onStatusChange(listing.listing_id, newStatus);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    if (!hasActualNoteContent(newNote) && mentionedUsers.length > 0) {
      setNoteError("Please add a message along with the mention");
      return;
    }
    setIsAddingNote(true);
    setNoteError(null);
    try {
      await onAddNote(listing.listing_id, newNote, mentionedUsers);
      setNewNote("");
      setMentionedUsers([]);
    } catch (err) {
      setNoteError(err.message || "Failed to add note");
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleMarkSold = async () => {
    setIsMarkingSold(true);
    try {
      await onMarkSold(listing.listing_id);
    } finally {
      setIsMarkingSold(false);
    }
  };

  const handleAssign = async (email, role, sendNotification) => {
    setIsAssigning(true);
    try {
      await onAssign(listing.listing_id, email, role, sendNotification);
      setShowAssignModal(false);
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="fixed right-0 top-0 h-full w-full max-w-lg bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white">
            <Home className="w-6 h-6" />
          </div>
          <div>
            <h2 className="font-bold text-lg text-slate-900 dark:text-white truncate max-w-[250px]">
              {listing.property_address}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {listing.city}, {listing.state} • {daysOnMarket} days on market
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Quick Actions */}
      <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-2">
        {/* Mark as Sold Button - Primary CTA */}
        {listing.status !== "sold" && (
          <button
            onClick={handleMarkSold}
            disabled={isMarkingSold}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {isMarkingSold ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <FileCheck className="w-4 h-4" />
                Mark as Sold
              </>
            )}
          </button>
        )}

        {listing.status === "sold" && listing.transaction_id && (
          <a
            href={`/transactions/${listing.transaction_id}`}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg text-sm font-medium hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors"
          >
            <FileText className="w-4 h-4" />
            View Transaction
            <ExternalLink className="w-3 h-3" />
          </a>
        )}

        {/* Status Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            disabled={isUpdatingStatus}
            className="flex items-center gap-2 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            {isUpdatingStatus ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <StatusBadge status={listing.status} />
            )}
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>
          {showStatusDropdown && (
            <>
              <div className="fixed inset-0 z-[5]" onClick={() => setShowStatusDropdown(false)} />
              <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-10 py-1">
                {(() => {
                  const validTransitions = getValidTransitions(listing.status);
                  const options = Object.entries(LISTING_STATUSES)
                    .filter(([key, value]) => validTransitions.includes(value));

                  if (options.length === 0) {
                    return (
                      <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                        No status changes available
                      </div>
                    );
                  }

                  return options.map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => handleStatusChange(value)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                    >
                      <StatusBadge status={value} />
                    </button>
                  ));
                })()}
              </div>
            </>
          )}
        </div>

        {/* Assign Button */}
        <button
          onClick={() => setShowAssignModal(true)}
          className="flex items-center gap-2 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
        >
          <UserPlus className="w-4 h-4 text-slate-500" />
          {listing.assigned_to ? listing.assigned_to.split("@")[0] : "Assign"}
        </button>

        {/* Share with Lender Button */}
        {onShareWithLender && (
          <button
            onClick={() => onShareWithLender(listing)}
            className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 hover:from-blue-500/20 hover:to-indigo-500/20 rounded-lg text-sm font-medium transition-all"
            title="Share with Lender"
          >
            <Send className="w-4 h-4" />
            Send to Lender
          </button>
        )}

        {/* Archive Button */}
        {listing.status !== "sold" && onArchive && (
          <button
            onClick={() => onArchive(listing)}
            className="flex items-center gap-2 px-3 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-sm font-medium transition-colors"
            title="Archive listing"
          >
            <Archive className="w-4 h-4" />
            Archive
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 dark:border-slate-800">
        {[
          { id: "details", label: "Details" },
          { id: "notes", label: "Notes", count: listing.notes?.length || 0 },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
              activeTab === tab.id
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-slate-200 dark:bg-slate-700 rounded-full">
                {tab.count}
              </span>
            )}
            {activeTab === tab.id && (
              <motion.div
                layoutId="listingActiveTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600 dark:bg-emerald-400"
              />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === "details" && (
          <div className="p-6 space-y-6">
            {/* Price & Key Info */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-slate-500 dark:text-slate-400">Listing Price</span>
                <StatusBadge status={listing.status} />
              </div>
              <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(listing.listing_price)}
              </p>
              {listing.price_per_sqft && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  ${listing.price_per_sqft}/sq ft
                </p>
              )}
            </div>

            {/* Property Details */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Property Details</h3>
              <div className="grid grid-cols-2 gap-3">
                {listing.bedrooms && (
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center gap-2">
                    <Bed className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Bedrooms</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{listing.bedrooms}</p>
                    </div>
                  </div>
                )}
                {listing.bathrooms && (
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center gap-2">
                    <Bath className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Bathrooms</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{listing.bathrooms}</p>
                    </div>
                  </div>
                )}
                {listing.square_feet && (
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center gap-2">
                    <Square className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Square Feet</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{listing.square_feet.toLocaleString()}</p>
                    </div>
                  </div>
                )}
                {listing.year_built && (
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Year Built</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{listing.year_built}</p>
                    </div>
                  </div>
                )}
                {listing.property_type && (
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Property Type</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{listing.property_type}</p>
                    </div>
                  </div>
                )}
                {listing.lot_size && (
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Lot Size</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{listing.lot_size.toLocaleString()} sq ft</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Seller Info */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Seller Information</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Users className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-700 dark:text-slate-300">{listing.seller_name || "N/A"}</span>
                </div>
                {listing.seller_email && (
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a href={`mailto:${listing.seller_email}`} className="text-emerald-600 dark:text-emerald-400 hover:underline">
                      {listing.seller_email}
                    </a>
                  </div>
                )}
                {listing.seller_phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <a href={`tel:${listing.seller_phone}`} className="text-emerald-600 dark:text-emerald-400 hover:underline">
                      {formatPhoneNumber(listing.seller_phone)}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline Info */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Timeline</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Listed</span>
                  <span className="text-slate-700 dark:text-slate-300">{formatListingDate(listing.listing_date)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Days on Market</span>
                  <span className="text-slate-700 dark:text-slate-300">{daysOnMarket} days</span>
                </div>
                {listing.expiration_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500 dark:text-slate-400">Expires</span>
                    <span className="text-slate-700 dark:text-slate-300">{formatListingDate(listing.expiration_date)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Last Updated</span>
                  <span className="text-slate-700 dark:text-slate-300">{formatListingDate(listing.updated_at)}</span>
                </div>
              </div>
            </div>

            {/* MLS Info */}
            {listing.mls_number && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">MLS Information</h3>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500 dark:text-slate-400">MLS Number</p>
                  <p className="text-sm font-mono font-medium text-slate-900 dark:text-white">{listing.mls_number}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "notes" && (
          <div className="p-6">
            {/* Add Note Input */}
            <div className="mb-4">
              <MentionInput
                value={newNote}
                onChange={setNewNote}
                onMentionsChange={setMentionedUsers}
                teamMembers={teamMembers}
                userEmail={userEmail}
                placeholder="Add a note... Type @ to mention team members"
                rows={3}
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {mentionedUsers.length > 0 && !hasActualNoteContent(newNote) ? (
                    <span className="text-amber-600 dark:text-amber-400">
                      Please add a message along with the mention
                    </span>
                  ) : mentionedUsers.length > 0 ? (
                    <span>
                      Mentioning: {mentionedUsers.map(u => u.name || u.email).join(', ')}
                    </span>
                  ) : null}
                </p>
                <button
                  onClick={handleAddNote}
                  disabled={!newNote.trim() || isAddingNote || (mentionedUsers.length > 0 && !hasActualNoteContent(newNote))}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isAddingNote ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <MessageSquare className="w-4 h-4" />
                  )}
                  Add Note
                </button>
              </div>
            </div>

            {/* Note Error Display */}
            {noteError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {noteError}
                  </div>
                  <button onClick={() => setNoteError(null)} className="text-red-400 hover:text-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Notes Timeline */}
            <div className="space-y-4">
              {listing.notes?.length > 0 ? (
                listing.notes.map((note, idx) => (
                  <div key={idx} className="relative pl-6 pb-4 border-l-2 border-slate-200 dark:border-slate-700 last:pb-0">
                    <div className="absolute left-[-5px] top-0 w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600" />
                    <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                      {editingNoteIndex === note.index ? (
                        <div>
                          <textarea
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none text-sm"
                            autoFocus
                          />
                          <div className="flex justify-end gap-2 mt-2">
                            <button
                              onClick={() => { setEditingNoteIndex(null); setEditingNoteText(""); }}
                              disabled={isEditingNote}
                              className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleEditNote(note.index)}
                              disabled={!editingNoteText.trim() || isEditingNote}
                              className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                            >
                              {isEditingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm text-slate-700 dark:text-slate-300 flex-1 whitespace-pre-wrap">
                              {renderTextWithMentions(note.note, teamMembers)}
                            </p>
                            {canEditNote(note) && (
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  onClick={() => { setEditingNoteIndex(note.index); setEditingNoteText(note.note); }}
                                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                  title="Edit note"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => setPendingDeleteNoteIndex(note.index)}
                                  disabled={isDeletingNote === note.index}
                                  className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                  title="Delete note"
                                >
                                  {isDeletingNote === note.index ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 dark:text-slate-400">
                            <span>{note.author || note.created_by}</span>
                            <span>•</span>
                            <span>{formatListingDate(note.timestamp || note.created_at)}</span>
                          </div>
                          {/* Reactions */}
                          <div className="flex items-center gap-2 mt-2">
                            {note.reactions?.map((reaction, rIdx) => (
                              <button
                                key={rIdx}
                                onClick={() => handleReaction(note.index, reaction.emoji)}
                                className="flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded-full text-xs hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                              >
                                <span>{reaction.emoji}</span>
                                <span className="text-slate-600 dark:text-slate-400">{reaction.users?.length || 0}</span>
                              </button>
                            ))}
                            <div className="relative">
                              <button
                                onClick={() => setShowQuickReactions(showQuickReactions === note.index ? null : note.index)}
                                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                              >
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                              {showQuickReactions === note.index && (
                                <div className="absolute bottom-full left-0 mb-1 flex gap-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-1 shadow-lg z-10">
                                  {QUICK_REACTIONS.map((emoji) => (
                                    <button
                                      key={emoji}
                                      onClick={() => handleReaction(note.index, emoji)}
                                      className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No notes yet</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Assign Modal */}
      <AnimatePresence>
        {showAssignModal && (
          <AssignListingModal
            isOpen={showAssignModal}
            onClose={() => setShowAssignModal(false)}
            onAssign={handleAssign}
            teamMembers={teamMembers}
            currentAssignee={listing.assigned_to}
            isAssigning={isAssigning}
          />
        )}
      </AnimatePresence>

      {/* Delete Note Modal */}
      <AnimatePresence>
        {pendingDeleteNoteIndex !== null && (
          <DeleteNoteModal
            isOpen={true}
            onClose={() => setPendingDeleteNoteIndex(null)}
            onConfirm={handleDeleteNote}
            isDeleting={isDeletingNote !== null}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Main Listings Dashboard Component
const ListingsDashboard = ({
  userEmail,
  teamMembers = [],
  onCreditsUpdate,
  userCredits = 0,
  featureFlags = {},
}) => {
  // Team context state
  const [teamContext, setTeamContext] = useState(null);
  const [teamContextLoading, setTeamContextLoading] = useState(true);

  // Tab state
  const [activeTab, setActiveTab] = useState("mylistings");

  // My Listings state
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState(null);

  // Computed stats from actual listings array
  const computedStats = useMemo(() => {
    if (!listings || listings.length === 0) return stats;

    const activeListings = listings.filter(listing => (listing.status || "").toLowerCase() !== "archive");
    const statusCounts = activeListings.reduce((acc, listing) => {
      const status = (listing.status || 'active').toLowerCase();
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return {
      total: activeListings.length,
      active: statusCounts.active || 0,
      pending: statusCounts.pending || 0,
      under_contract: statusCounts.under_contract || 0,
      sold: statusCounts.sold || 0,
      expired: statusCounts.expired || 0,
      withdrawn: statusCounts.withdrawn || 0,
    };
  }, [listings, stats]);

  // Archive state
  const [archivedListings, setArchivedListings] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archiveSearch, setArchiveSearch] = useState("");
  const [pendingArchiveListing, setPendingArchiveListing] = useState(null);
  const [archiveReasonSelect, setArchiveReasonSelect] = useState("");
  const [archiveReasonCustom, setArchiveReasonCustom] = useState("");
  const [isArchiving, setIsArchiving] = useState(false);
  const [pendingRestoreListing, setPendingRestoreListing] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [ownershipFilter, setOwnershipFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("listing_date");
  const [sortDirection, setSortDirection] = useState("desc");

  // Success modal state
  const [successModal, setSuccessModal] = useState({ isOpen: false, type: null, data: null });

  // Add Listing modal state
  const [showAddListingModal, setShowAddListingModal] = useState(false);
  const [isAddingListing, setIsAddingListing] = useState(false);

  // Sales celebration
  const { triggerCelebration, celebrationState } = useSalesCelebration();

  // Calculate total pipeline value
  const pipelineStats = useMemo(() => {
    const parseValue = (listing) => {
      const val = listing?.listing_price;
      if (!val) return 0;
      const parsed = parseFloat(String(val).replace(/[^0-9.-]/g, ""));
      return isNaN(parsed) ? 0 : parsed;
    };

    const activeStatuses = ["active", "pending", "under_contract"];
    const activePipeline = listings.filter((listing) => activeStatuses.includes(listing.status));
    const totalValue = listings.reduce((sum, listing) => sum + parseValue(listing), 0);
    const activeValue = activePipeline.reduce((sum, listing) => sum + parseValue(listing), 0);

    return { totalValue, activeValue, activeCount: activePipeline.length };
  }, [listings]);

  // Load listings
  const loadListings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [listingsData, statsData] = await Promise.all([
        fetchListings({ include_team: true, limit: 100 }),
        fetchListingStats({ include_team: true }),
      ]);
      setListings(listingsData.listings || []);
      setStats(statsData);
    } catch (err) {
      console.error("Failed to load listings:", err);
      setError(err.message || "Failed to load listings");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle add listing
  const handleAddListing = async (listingData) => {
    setIsAddingListing(true);
    try {
      const newListing = await createListing(listingData);
      setListings((prev) => [newListing, ...prev]);
      setShowAddListingModal(false);
      // Refresh stats
      const statsData = await fetchListingStats({ include_team: true });
      setStats(statsData);
    } catch (err) {
      console.error("Failed to add listing:", err);
      throw err;
    } finally {
      setIsAddingListing(false);
    }
  };

  // Load team context
  const loadTeamContext = useCallback(async () => {
    setTeamContextLoading(true);
    try {
      const response = await fetchTeamContext();
      if (response.success && response.data) {
        setTeamContext(response.data);
      }
    } catch (err) {
      console.error("Failed to load team context:", err);
    } finally {
      setTeamContextLoading(false);
    }
  }, []);

  // Load archived listings
  const loadArchivedListings = useCallback(async () => {
    setArchivedLoading(true);
    try {
      const data = await fetchArchivedListings({ include_team: true });
      setArchivedListings(data.listings || []);
    } catch (err) {
      console.error("Failed to load archived listings:", err);
    } finally {
      setArchivedLoading(false);
    }
  }, []);

  // Handle archive listing
  const handleArchiveListing = async () => {
    if (!pendingArchiveListing || isArchiving) return;
    const reason = archiveReasonSelect === "Other" ? archiveReasonCustom : archiveReasonSelect;

    setIsArchiving(true);
    try {
      await archiveListing(pendingArchiveListing.listing_id, reason);
      setListings((prev) => prev.filter((l) => l.listing_id !== pendingArchiveListing.listing_id));
      loadArchivedListings();
      setPendingArchiveListing(null);
      setArchiveReasonSelect("");
      setArchiveReasonCustom("");
    } catch (err) {
      console.error("Failed to archive listing:", err);
      alert(err.message || "Failed to archive listing");
    } finally {
      setIsArchiving(false);
    }
  };

  // Handle restore listing
  const handleRestoreListing = async () => {
    if (!pendingRestoreListing || isRestoring) return;

    setIsRestoring(true);
    try {
      await restoreListing(pendingRestoreListing.listing_id);
      setArchivedListings((prev) => prev.filter((l) => l.listing_id !== pendingRestoreListing.listing_id));
      loadListings();
      setPendingRestoreListing(null);
    } catch (err) {
      console.error("Failed to restore listing:", err);
      alert(err.message || "Failed to restore listing");
    } finally {
      setIsRestoring(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (listingId, newStatus) => {
    try {
      const updatedListing = await updateListingStatus(listingId, newStatus);
      setListings((prev) =>
        prev.map((l) => (l.listing_id === listingId ? { ...l, ...updatedListing } : l))
      );
      if (selectedListing?.listing_id === listingId) {
        setSelectedListing((prev) => ({ ...prev, ...updatedListing }));
      }
    } catch (err) {
      console.error("Failed to update status:", err);
      alert(err.message || "Failed to update status");
    }
  };

  // Handle add note
  const handleAddNote = async (listingId, note, mentionedUsers) => {
    const updatedListing = await addListingNote(listingId, note, mentionedUsers);
    setListings((prev) =>
      prev.map((l) => (l.listing_id === listingId ? { ...l, ...updatedListing } : l))
    );
    if (selectedListing?.listing_id === listingId) {
      setSelectedListing((prev) => ({ ...prev, ...updatedListing }));
    }
  };

  // Handle update note
  const handleUpdateNote = async (listingId, noteIndex, note) => {
    const updatedListing = await updateListingNote(listingId, noteIndex, note);
    setListings((prev) =>
      prev.map((l) => (l.listing_id === listingId ? { ...l, ...updatedListing } : l))
    );
    if (selectedListing?.listing_id === listingId) {
      setSelectedListing((prev) => ({ ...prev, ...updatedListing }));
    }
  };

  // Handle delete note
  const handleDeleteNote = async (listingId, noteIndex) => {
    const updatedListing = await deleteListingNote(listingId, noteIndex);
    setListings((prev) =>
      prev.map((l) => (l.listing_id === listingId ? { ...l, ...updatedListing } : l))
    );
    if (selectedListing?.listing_id === listingId) {
      setSelectedListing((prev) => ({ ...prev, ...updatedListing }));
    }
  };

  // Handle toggle reaction
  const handleToggleReaction = async (listingId, noteIndex, emoji) => {
    const updatedListing = await toggleNoteReaction(listingId, noteIndex, emoji);
    setListings((prev) =>
      prev.map((l) => (l.listing_id === listingId ? { ...l, ...updatedListing } : l))
    );
    if (selectedListing?.listing_id === listingId) {
      setSelectedListing((prev) => ({ ...prev, ...updatedListing }));
    }
  };

  // Handle assign
  const handleAssign = async (listingId, email, role, sendNotification) => {
    const updatedListing = await assignListing(listingId, email, role, sendNotification);
    setListings((prev) =>
      prev.map((l) => (l.listing_id === listingId ? { ...l, ...updatedListing } : l))
    );
    if (selectedListing?.listing_id === listingId) {
      setSelectedListing((prev) => ({ ...prev, ...updatedListing }));
    }
  };

  // Handle mark as sold
  const handleMarkSold = async (listingId) => {
    try {
      const result = await closeListing(listingId, {});
      setListings((prev) =>
        prev.map((l) => (l.listing_id === listingId ? { ...l, status: "sold" } : l))
      );
      if (selectedListing?.listing_id === listingId) {
        setSelectedListing((prev) => ({ ...prev, status: "sold" }));
      }
      triggerCelebration();
      setSuccessModal({ isOpen: true, type: "sold", data: result });
    } catch (err) {
      console.error("Failed to mark as sold:", err);
      alert(err.message || "Failed to mark listing as sold");
    }
  };

  // Filter and sort listings
  const filteredListings = useMemo(() => {
    let result = [...listings];

    // Status filter
    if (statusFilter) {
      result = result.filter((l) => l.status === statusFilter);
    }

    // Ownership filter
    if (ownershipFilter === "mine") {
      result = result.filter((l) =>
        l.is_own_listing === true ||
        l.assigned_to === userEmail ||
        l.owner_email === userEmail
      );
    } else if (ownershipFilter === "team") {
      result = result.filter((l) =>
        l.is_own_listing !== true &&
        l.assigned_to !== userEmail &&
        l.owner_email !== userEmail
      );
    }

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((l) =>
        l.property_address?.toLowerCase().includes(query) ||
        l.city?.toLowerCase().includes(query) ||
        l.state?.toLowerCase().includes(query) ||
        l.zip_code?.includes(query) ||
        l.seller_name?.toLowerCase().includes(query)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case "address":
          aVal = a.property_address || "";
          bVal = b.property_address || "";
          break;
        case "price":
          aVal = a.listing_price || 0;
          bVal = b.listing_price || 0;
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "days_on_market":
          aVal = calculateDaysOnMarket(a.listing_date);
          bVal = calculateDaysOnMarket(b.listing_date);
          break;
        case "listing_date":
        default:
          aVal = new Date(a.listing_date || 0).getTime();
          bVal = new Date(b.listing_date || 0).getTime();
          break;
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });

    return result;
  }, [listings, statusFilter, ownershipFilter, searchQuery, sortField, sortDirection, userEmail]);

  // Initial load
  useEffect(() => {
    loadListings();
    loadTeamContext();
  }, [loadListings, loadTeamContext]);

  // Load archived when tab changes
  useEffect(() => {
    if (activeTab === "archived") {
      loadArchivedListings();
    }
  }, [activeTab, loadArchivedListings]);

  // Toggle sort
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
    return sortDirection === "asc" ? (
      <ArrowUp className="w-3 h-3 text-emerald-500" />
    ) : (
      <ArrowDown className="w-3 h-3 text-emerald-500" />
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Celebration Overlay */}
      <SalesCelebrationOverlay state={celebrationState} />

      {/* Success Modal */}
      <AnimatePresence>
        {successModal.isOpen && (
          <SuccessModal
            isOpen={successModal.isOpen}
            onClose={() => setSuccessModal({ isOpen: false, type: null, data: null })}
            type={successModal.type}
            data={successModal.data}
          />
        )}
      </AnimatePresence>

      {/* Add Listing Modal */}
      <AnimatePresence>
        {showAddListingModal && (
          <AddListingModal
            isOpen={showAddListingModal}
            onClose={() => setShowAddListingModal(false)}
            onAdd={handleAddListing}
            isAdding={isAddingListing}
          />
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Listings</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Manage your real estate listings
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadListings}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowAddListingModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Listing
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="flex gap-4 overflow-x-auto pb-4 mb-6">
          <StatsCard
            label="Active"
            value={computedStats?.active || 0}
            color="emerald"
            icon={Home}
            isActive={statusFilter === "active"}
            onClick={() => setStatusFilter(statusFilter === "active" ? null : "active")}
          />
          <StatsCard
            label="Pending"
            value={computedStats?.pending || 0}
            color="amber"
            icon={Clock}
            isActive={statusFilter === "pending"}
            onClick={() => setStatusFilter(statusFilter === "pending" ? null : "pending")}
          />
          <StatsCard
            label="Under Contract"
            value={computedStats?.under_contract || 0}
            color="blue"
            icon={FileCheck}
            isActive={statusFilter === "under_contract"}
            onClick={() => setStatusFilter(statusFilter === "under_contract" ? null : "under_contract")}
          />
          <StatsCard
            label="Sold"
            value={computedStats?.sold || 0}
            color="violet"
            icon={Trophy}
            isActive={statusFilter === "sold"}
            onClick={() => setStatusFilter(statusFilter === "sold" ? null : "sold")}
          />
          <StatsCard
            label="Total Pipeline"
            value={formatCurrencyAbbreviated(pipelineStats.activeValue)}
            color="slate"
            icon={TrendingUp}
            isActive={false}
            onClick={() => {}}
          />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-800 mb-6">
          {[
            { id: "mylistings", label: "My Listings", count: computedStats?.total },
            { id: "archived", label: "Archived", count: archivedListings.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                activeTab === tab.id
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-slate-200 dark:bg-slate-700 rounded-full">
                  {tab.count}
                </span>
              )}
              {activeTab === tab.id && (
                <motion.div
                  layoutId="listingsDashboardTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600 dark:bg-emerald-400"
                />
              )}
            </button>
          ))}
        </div>

        {/* My Listings Tab Content */}
        {activeTab === "mylistings" && (
          <>
            {/* Search & Filters */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search listings..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <select
                value={ownershipFilter}
                onChange={(e) => setOwnershipFilter(e.target.value)}
                className="px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Listings</option>
                <option value="mine">My Listings</option>
                <option value="team">Team Listings</option>
              </select>
            </div>

            {/* Listings Table */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-16 text-red-500">
                  <AlertCircle className="w-8 h-8 mb-2" />
                  <p>{error}</p>
                  <button
                    onClick={loadListings}
                    className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                  >
                    Retry
                  </button>
                </div>
              ) : filteredListings.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-500 dark:text-slate-400">
                  <Home className="w-12 h-12 mb-4 opacity-50" />
                  <p className="text-lg font-medium">No listings found</p>
                  <p className="text-sm">Try adjusting your filters or add a new listing</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          <button onClick={() => toggleSort("address")} className="flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300">
                            Property <SortIcon field="address" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          <button onClick={() => toggleSort("price")} className="flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300">
                            Price <SortIcon field="price" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          <button onClick={() => toggleSort("status")} className="flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300">
                            Status <SortIcon field="status" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Agent
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Details
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          <button onClick={() => toggleSort("days_on_market")} className="flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300">
                            DOM <SortIcon field="days_on_market" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Source
                        </th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {filteredListings.map((listing) => (
                        <ListingRow
                          key={listing.listing_id}
                          listing={listing}
                          isSelected={selectedListing?.listing_id === listing.listing_id}
                          onClick={() => setSelectedListing(listing)}
                          userEmail={userEmail}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Archived Tab Content */}
        {activeTab === "archived" && (
          <>
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search archived listings..."
                  value={archiveSearch}
                  onChange={(e) => setArchiveSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              {archivedLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
              ) : archivedListings.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-500 dark:text-slate-400">
                  <Archive className="w-12 h-12 mb-4 opacity-50" />
                  <p className="text-lg font-medium">No archived listings</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {archivedListings
                    .filter((l) =>
                      !archiveSearch ||
                      l.property_address?.toLowerCase().includes(archiveSearch.toLowerCase())
                    )
                    .map((listing) => (
                      <div key={listing.listing_id} className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                            <Home className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{listing.property_address}</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Archived: {formatListingDate(listing.archived_at)} • {listing.archive_reason || "No reason"}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setPendingRestoreListing(listing)}
                          className="flex items-center gap-2 px-3 py-2 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                        >
                          <RotateCcw className="w-4 h-4" />
                          Restore
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Detail Panel */}
      <AnimatePresence>
        {selectedListing && (
          <ListingDetailPanel
            listing={selectedListing}
            onClose={() => setSelectedListing(null)}
            onStatusChange={handleStatusChange}
            onAddNote={handleAddNote}
            onUpdateNote={handleUpdateNote}
            onDeleteNote={handleDeleteNote}
            onToggleReaction={handleToggleReaction}
            onAssign={handleAssign}
            onMarkSold={handleMarkSold}
            onArchive={(listing) => setPendingArchiveListing(listing)}
            teamMembers={teamMembers}
            userEmail={userEmail}
          />
        )}
      </AnimatePresence>

      {/* Archive Modal */}
      <AnimatePresence>
        {pendingArchiveListing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="p-6">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Archive className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white text-center mb-2">
                  Archive Listing?
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-4">
                  {pendingArchiveListing.property_address}
                </p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Reason for archiving
                  </label>
                  <select
                    value={archiveReasonSelect}
                    onChange={(e) => setArchiveReasonSelect(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  >
                    <option value="">Select reason...</option>
                    {ARCHIVE_REASONS.map((reason) => (
                      <option key={reason} value={reason}>{reason}</option>
                    ))}
                  </select>
                </div>
                {archiveReasonSelect === "Other" && (
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Enter reason..."
                      value={archiveReasonCustom}
                      onChange={(e) => setArchiveReasonCustom(e.target.value)}
                      className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                    />
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setPendingArchiveListing(null);
                      setArchiveReasonSelect("");
                      setArchiveReasonCustom("");
                    }}
                    disabled={isArchiving}
                    className="flex-1 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleArchiveListing}
                    disabled={isArchiving || (!archiveReasonSelect || (archiveReasonSelect === "Other" && !archiveReasonCustom))}
                    className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isArchiving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Archiving...
                      </>
                    ) : (
                      "Archive"
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Restore Confirmation Modal */}
      <AnimatePresence>
        {pendingRestoreListing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-sm overflow-hidden"
            >
              <div className="p-6">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <RotateCcw className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white text-center mb-2">
                  Restore Listing?
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-6">
                  This will move the listing back to your active listings.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setPendingRestoreListing(null)}
                    disabled={isRestoring}
                    className="flex-1 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRestoreListing}
                    disabled={isRestoring}
                    className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isRestoring ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Restoring...
                      </>
                    ) : (
                      "Restore"
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ListingsDashboard;
